import * as Sentry from '@sentry/react';

// ---------------------------------------------------------------------------
// PII-aware meta sanitizer
// ---------------------------------------------------------------------------

// Mirrors apps/api/src/instrument.ts SENSITIVE_KEY_PATTERN so the two surfaces
// redact the same keys. `email` is deliberately excluded (identity policy —
// see design doc PII posture section).
const SENSITIVE_KEY_PATTERN =
  /password|passwd|secret|credential|authorization|token|cookie|ssn|api[-_]?key|private[-_]?key/i;

const REDACTED = '[redacted]';
const MAX_VALUE_LENGTH = 256;

type PlainMeta = Record<string, unknown>;

/**
 * Defensive, shallow sanitizer for caller-supplied breadcrumb/extra data.
 * Mirrors the API's sanitizeMeta (apps/api/src/monitoring/monitoring.service.ts):
 * - keys matching sensitive patterns are redacted;
 * - objects AND arrays are JSON-serialized and truncated to 256 chars so a
 *   caller cannot ship a huge/deeply-nested payload or leak an array of secrets;
 * - primitives pass through unchanged.
 * `email` is intentionally left unredacted (deliberate identity field).
 */
function sanitizeMeta(meta: PlainMeta): PlainMeta {
  const result: PlainMeta = {};
  for (const [key, value] of Object.entries(meta)) {
    if (SENSITIVE_KEY_PATTERN.test(key)) {
      result[key] = REDACTED;
    } else if (value === null || value === undefined) {
      result[key] = value;
    } else if (typeof value === 'object') {
      let serialized: string;
      try {
        serialized = JSON.stringify(value);
      } catch {
        serialized = '[unserializable]';
      }
      result[key] =
        serialized.length > MAX_VALUE_LENGTH
          ? `${serialized.slice(0, MAX_VALUE_LENGTH)}…[truncated]`
          : serialized;
    } else {
      result[key] = value;
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Auth events
// ---------------------------------------------------------------------------

export type AuthEventKind =
  | 'login'
  | 'logout'
  | 'failed-login'
  | 'session-restored'
  | 'session-expired';

/**
 * Record an auth lifecycle event as a breadcrumb.
 * Failed-login events are also sent as a warning-level captureMessage so they
 * appear as issues and can trigger brute-force alert rules.
 */
export function trackAuthEvent(kind: AuthEventKind, meta?: PlainMeta): void {
  const data = meta ? sanitizeMeta(meta) : undefined;

  Sentry.addBreadcrumb({
    category: 'auth',
    message: kind,
    data,
    level: 'info',
  });

  if (kind === 'failed-login') {
    Sentry.captureMessage(`auth: ${kind}`, {
      level: 'warning',
      tags: { category: 'auth' },
      extra: data,
    });
  }
}

// ---------------------------------------------------------------------------
// Flow events
// ---------------------------------------------------------------------------

export type FlowEventKind =
  | 'flow-opened'
  | 'flow-created'
  | 'flow-updated'
  | 'flow-deleted'
  | 'flow-published';

/**
 * Record a flow lifecycle event as a breadcrumb.
 */
export function trackFlowEvent(kind: FlowEventKind, meta?: PlainMeta): void {
  const data = meta ? sanitizeMeta(meta) : undefined;

  Sentry.addBreadcrumb({
    category: 'flow',
    message: kind,
    data,
    level: 'info',
  });
}

// ---------------------------------------------------------------------------
// API error capture
// ---------------------------------------------------------------------------

/**
 * Capture a degraded frontend condition — an API call that failed or returned
 * an error response that was caught and handled by the UI. This surfaces
 * silent failures that would otherwise never reach the global error boundary.
 */
export function captureApiError(
  error: unknown,
  context?: PlainMeta,
): void {
  let safeContext = context;
  if (context && 'url' in context && context.url != null) {
    // Strip any query string from a URL value before it is captured. sanitizeMeta
    // matches on keys, not value substrings, so a token in the query (e.g.
    // /auth/reset-password?token=abc) would otherwise ship unredacted, and the
    // web SDK has no beforeSend backstop. Do this for all callers, not just the
    // current api-client site.
    safeContext = { ...context, url: String(context.url).split('?')[0] };
  }
  const extra = safeContext ? sanitizeMeta(safeContext) : undefined;
  Sentry.captureException(error, {
    level: 'warning',
    tags: { category: 'api' },
    extra,
  });
}
